import Editor, { type OnMount } from "@monaco-editor/react";
import type * as Monaco from "monaco-editor";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Split } from "../components/Split";
import { KIND_ICON, TriggerGraphView } from "../graph/TriggerGraphView";
import { KIND_NAMES, nodeAt, type GraphNode, type GraphOverlay, type NodeKind, type TriggerGraph } from "../graph/types";
import { useTriggerGraph } from "../graph/useTriggerGraph";
import "../editor/monacoSetup";
import "./workbench.css";

export interface TriggerWorkbenchProps {
  filename: string;
  source: string;
  onChange?: (source: string) => void;
  readOnly?: boolean;
  /** Optional trace overlay for the current second (Analysis / live mode). */
  overlay?: GraphOverlay;
  /** Pre-derived graph (skips the API); otherwise derived from `source`. */
  graph?: TriggerGraph | null;
  /** Extra toolbar content (Run, Step, ...). */
  toolbar?: React.ReactNode;
  onGraph?: (graph: TriggerGraph) => void;
  /** Lint problems (from the backend) shown as editor markers next to syntax errors. */
  problems?: { severity: "error" | "warning" | "info"; line: number; col: number; message: string; code?: string }[];
  onEditorMount?: (editor: Monaco.editor.IStandaloneCodeEditor, monaco: typeof Monaco) => void;
  onSave?: () => void;
  theme?: "dark" | "light";
}

const DEFAULT_HIDDEN = new Set<string>();
const PLAIN_KEY = "rs.graph.plain";

const PHASE_SHORT: Record<string, string> = { setup: "at start", tick: "every second", finish: "at end" };

const LEGEND: { kind: NodeKind; what: string }[] = [
  { kind: "param", what: "a recorded flight parameter" },
  { kind: "input", what: "a value from the inputs (settings) file" },
  { kind: "compare", what: "a check that is true or false each second" },
  { kind: "boolean", what: "combines several checks" },
  { kind: "temporal", what: "looks at how a check behaves over time" },
  { kind: "branch", what: "a decision: what happens when a check is true" },
  { kind: "snapshot", what: "captures a moment of the flight for the report" },
  { kind: "control", what: "starts / stops watching" },
  { kind: "event", what: "notes something in the run log" },
  { kind: "state", what: "a value the trigger remembers between seconds" },
  { kind: "dependency", what: "another trigger this one relies on" },
  { kind: "context", what: "information about the flight itself" },
  { kind: "custom", what: "plain Python the graph does not interpret" },
];

export function TriggerWorkbench({
  filename,
  source,
  onChange,
  readOnly,
  overlay,
  graph: givenGraph,
  toolbar,
  onGraph,
  problems,
  onEditorMount,
  onSave,
  theme = "dark",
}: TriggerWorkbenchProps) {
  const saveRef = useRef(onSave);
  saveRef.current = onSave;
  const query = useTriggerGraph(source, filename);
  const graph = givenGraph ?? query.data ?? null;
  const editorRef = useRef<Monaco.editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<typeof Monaco | null>(null);
  const decorations = useRef<Monaco.editor.IEditorDecorationsCollection | null>(null);
  const [cursor, setCursor] = useState<{ line: number; col: number } | null>(null);
  const [clickedNode, setClickedNode] = useState<string | null>(null);
  const [hovered, setHovered] = useState<GraphNode | null>(null);
  const [hideCustom, setHideCustom] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [plain, setPlain] = useState<boolean>(() => localStorage.getItem(PLAIN_KEY) !== "0");
  const [legend, setLegend] = useState(false);
  useEffect(() => {
    localStorage.setItem(PLAIN_KEY, plain ? "1" : "0");
  }, [plain]);

  useEffect(() => {
    if (graph && onGraph) onGraph(graph);
  }, [graph, onGraph]);

  // cursor -> node
  const cursorNode = useMemo(() => {
    if (!graph || !cursor) return null;
    return nodeAt(graph, cursor.line, cursor.col);
  }, [graph, cursor]);
  const selectedNodeId = clickedNode ?? cursorNode?.id ?? null;

  const highlightRange = useCallback((node: GraphNode | null) => {
    const ed = editorRef.current;
    const monaco = monacoRef.current;
    if (!ed || !monaco) return;
    if (!decorations.current) decorations.current = ed.createDecorationsCollection();
    if (!node?.range) {
      decorations.current.clear();
      return;
    }
    const r = node.range;
    decorations.current.set([
      {
        range: new monaco.Range(r.line, r.col + 1, r.end_line, r.end_col + 1),
        options: { className: "rs-code-highlight", isWholeLine: false },
      },
      {
        range: new monaco.Range(r.line, 1, r.line, 1),
        options: { isWholeLine: true, linesDecorationsClassName: "rs-code-gutter" },
      },
    ]);
  }, []);

  useEffect(() => {
    const node = graph?.nodes.find((n) => n.id === selectedNodeId) ?? null;
    highlightRange(hovered ?? node);
  }, [graph, selectedNodeId, hovered, highlightRange]);

  // node -> code
  const jumpTo = useCallback((node: GraphNode) => {
    const ed = editorRef.current;
    const monaco = monacoRef.current;
    if (!ed || !monaco || !node.range) return;
    const r = node.range;
    setClickedNode(node.id);
    ed.revealRangeInCenterIfOutsideViewport(new monaco.Range(r.line, r.col + 1, r.end_line, r.end_col + 1));
    ed.setSelection(new monaco.Range(r.line, r.col + 1, r.end_line, r.end_col + 1));
    ed.focus();
  }, []);

  const onMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    editor.onDidChangeCursorPosition((e) => {
      setClickedNode(null);
      setCursor({ line: e.position.lineNumber, col: e.position.column - 1 });
    });
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => saveRef.current?.());
    onEditorMount?.(editor, monaco);
  };

  // syntax errors + lint problems -> editor markers
  useEffect(() => {
    const ed = editorRef.current;
    const monaco = monacoRef.current;
    const model = ed?.getModel();
    if (!ed || !monaco || !model) return;
    const sev = { error: monaco.MarkerSeverity.Error, warning: monaco.MarkerSeverity.Warning, info: monaco.MarkerSeverity.Info };
    const syntax = (graph?.errors ?? []).map((e) => ({
      severity: monaco.MarkerSeverity.Error,
      message: e.message,
      startLineNumber: e.line || 1,
      startColumn: (e.col || 0) + 1,
      endLineNumber: e.line || 1,
      endColumn: (e.col || 0) + 2,
    }));
    const lint = (problems ?? [])
      .filter((p) => !(graph?.errors ?? []).some((e) => e.line === p.line && e.message === p.message))
      .map((p) => ({
        severity: sev[p.severity],
        message: p.code ? `${p.message} (${p.code})` : p.message,
        startLineNumber: p.line || 1,
        startColumn: (p.col || 0) + 1,
        endLineNumber: p.line || 1,
        endColumn: model.getLineMaxColumn(Math.min(Math.max(p.line || 1, 1), model.getLineCount())),
      }));
    monaco.editor.setModelMarkers(model, "reports-studio", [...syntax, ...lint]);
  }, [graph, problems]);

  const hiddenKinds = useMemo(() => (hideCustom ? new Set(["custom"]) : DEFAULT_HIDDEN), [hideCustom]);
  const phases = useMemo(() => {
    const out: { id: string; label: string; title: string }[] = [];
    const many = (graph?.triggers.length ?? 0) > 1;
    for (const t of graph?.triggers ?? []) {
      for (const p of Object.keys(t.phases)) {
        const word = plain ? (PHASE_SHORT[p] ?? p) : p;
        out.push({
          id: `group:${t.name}/${p}`,
          label: many ? `${t.name} · ${word}` : word,
          title: `${t.name} · ${p}: click to hide / show this part of the graph`,
        });
      }
    }
    return out;
  }, [graph, plain]);

  const togglePhase = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selected = graph?.nodes.find((n) => n.id === selectedNodeId) ?? null;

  return (
    <div className="rs-workbench">
      <div className="rs-workbench-toolbar">
        <span className="rs-file">{filename}</span>
        {toolbar}
        <span className="rs-spacer" />
        {query.isFetching && !givenGraph ? <span className="rs-dim">updating graph…</span> : null}
      </div>
      <Split id="workbench" className="rs-workbench-body fill" sizes={["1fr", "1.2fr"]} minSizes={[280, 240]} collapsible={[true, true]}>
        <div className="rs-editor">
          <Editor
            path={filename}
            language="python"
            theme={theme === "dark" ? "rs-dark" : "rs-light"}
            value={source}
            onChange={(v) => onChange?.(v ?? "")}
            onMount={onMount}
            options={{
              readOnly,
              minimap: { enabled: false },
              fontSize: 13,
              fontFamily: '"Cascadia Code", "JetBrains Mono", Consolas, monospace',
              scrollBeyondLastLine: false,
              renderLineHighlight: "line",
              glyphMargin: true,
              automaticLayout: true,
            }}
          />
        </div>
        <div className="rs-graph-pane">
          <div className="rs-graph-head">
            <div className="rs-seg" role="group" aria-label="graph wording">
              <button className={plain ? "rs-seg-on" : ""} onClick={() => setPlain(true)} title="describe each step in plain words">
                Plain words
              </button>
              <button className={!plain ? "rs-seg-on" : ""} onClick={() => setPlain(false)} title="show the code expression on each step">
                Code
              </button>
            </div>
            {graph?.triggers.length ? (
              <span className="rs-graph-head-sep" aria-hidden />
            ) : null}
            {phases.map((p) => (
              <button key={p.id} className={`rs-chip${collapsed.has(p.id) ? " rs-chip-off" : ""}`} onClick={() => togglePhase(p.id)} title={p.title}>
                {p.label}
              </button>
            ))}
            <span className="rs-spacer" />
            <label className="rs-toggle" title="hide boxes that are plain Python (no trigger meaning)">
              <input type="checkbox" checked={hideCustom} onChange={(e) => setHideCustom(e.target.checked)} /> hide custom code
            </label>
            <button className={`rs-chip rs-chip-help${legend ? " rs-chip-on" : ""}`} onClick={() => setLegend((v) => !v)} title="what the shapes and colours mean">
              ? legend
            </button>
          </div>
          {legend ? <GraphLegend onClose={() => setLegend(false)} /> : null}
          <TriggerGraphView
            graph={graph}
            selectedNodeId={selectedNodeId}
            onNodeClick={jumpTo}
            onNodeHover={setHovered}
            overlay={overlay}
            hiddenKinds={hiddenKinds}
            collapsedPhases={collapsed}
            plain={plain}
          />
          {selected ? <NodeInspector node={selected} graph={graph!} overlay={overlay} plain={plain} /> : null}
        </div>
      </Split>
    </div>
  );
}

function GraphLegend({ onClose }: { onClose: () => void }) {
  return (
    <div className="rs-legend">
      <div className="rs-legend-head">
        <strong>Reading the graph</strong>
        <span className="rs-dim">left to right: values flow into checks, checks into decisions, decisions into snapshots and reports</span>
        <button className="rs-chip" onClick={onClose}>
          close
        </button>
      </div>
      <div className="rs-legend-grid">
        {LEGEND.map((l) => (
          <div key={l.kind} className="rs-legend-row">
            <span className={`rs-legend-icon rs-legend-box rs-kind-${l.kind}`}>{KIND_ICON[l.kind]}</span>
            <span className="rs-legend-name">{KIND_NAMES[l.kind]}</span>
            <span className="rs-dim">{l.what}</span>
          </div>
        ))}
        <div className="rs-legend-row">
          <span className="rs-legend-icon rs-legend-swatch rs-legend-true" />
          <span className="rs-legend-name">green / red</span>
          <span className="rs-dim">while a run is replayed: the check was true / false at that second</span>
        </div>
        <div className="rs-legend-row">
          <span className="rs-legend-icon rs-legend-swatch rs-legend-dashed" />
          <span className="rs-legend-name">dashed box</span>
          <span className="rs-dim">not recorded second by second (no value shown when replaying)</span>
        </div>
      </div>
    </div>
  );
}

const PARAM_WORDS: Record<string, string> = {
  threshold: "compared with",
  op: "comparison",
  min: "lower bound",
  max: "upper bound",
  seconds: "must hold for (s)",
  window: "window",
  tol: "tolerance",
  helper: "function",
  label: "snapshot name",
  payload_keys: "records",
  reports: "reports",
  reports_expr: "reports",
  mnemonic: "parameter",
  path: "setting",
  key: "remembered as",
  trigger: "trigger",
  call: "call",
  kind: "kind",
  test: "condition",
  args: "arguments",
  end_of_flight: "decided at end of flight",
  external: "defined elsewhere",
};

const HIDDEN_PARAMS = new Set(["bindings"]);
/* redundant with "in code" / the kind pill when reading in plain words */
const PLAIN_HIDDEN_PARAMS = new Set(["python", "dynamic", "derived", "write", "comprehension", "attr", "value", "report", "helper", "call", "op", "kind"]);

function NodeInspector({ node, graph, overlay, plain }: { node: GraphNode; graph: TriggerGraph; overlay?: GraphOverlay; plain: boolean }) {
  const inputs = graph.edges.filter((e) => e.target === node.id && e.kind === "data").map((e) => graph.nodes.find((n) => n.id === e.source)).filter(Boolean) as GraphNode[];
  const params = Object.entries(node.params).filter(
    ([k, v]) => v !== null && v !== undefined && !HIDDEN_PARAMS.has(k) && !(plain && (v === false || PLAIN_HIDDEN_PARAMS.has(k))),
  );
  const value = overlay?.[node.id];
  const showText = plain && node.text;
  return (
    <div className="rs-inspector">
      <div className="rs-inspector-title">
        <span className={`rs-kind-pill rs-kind-${node.kind}`}>{plain ? KIND_NAMES[node.kind] : node.kind}</span>
        {showText ? <span className="rs-inspector-text">{node.text}</span> : <span className="rs-mono">{node.label}</span>}
        {node.badge ? <span className="rs-badge">{node.badge}</span> : null}
      </div>
      <div className="rs-inspector-grid">
        {showText ? (
          <>
            <span>in code</span>
            <span className="rs-mono">{node.label}</span>
          </>
        ) : null}
        {node.phase ? (
          <>
            <span>{plain ? "runs" : "phase"}</span>
            <span>{plain ? (PHASE_SHORT[node.phase] ?? node.phase) : node.phase}</span>
          </>
        ) : null}
        {node.range ? (
          <>
            <span>{plain ? "line" : "source"}</span>
            <span className="rs-mono">
              {plain
                ? node.range.line === node.range.end_line
                  ? `${node.range.line}`
                  : `${node.range.line}–${node.range.end_line}`
                : `L${node.range.line}:${node.range.col}–L${node.range.end_line}:${node.range.end_col}`}
            </span>
          </>
        ) : null}
        {!plain ? (
          <>
            <span>id</span>
            <span className="rs-mono">{node.id}</span>
            <span>traced</span>
            <span>{node.traced ? `yes (${node.runtime_kind})` : "no"}</span>
          </>
        ) : null}
        {value !== undefined ? (
          <>
            <span>{plain ? "value now" : "value"}</span>
            <span className="rs-mono">{String(value)}</span>
          </>
        ) : null}
        {params.map(([k, v]) => (
          <>
            <span key={`${k}-k`}>{plain ? (PARAM_WORDS[k] ?? k) : k}</span>
            <span key={`${k}-v`} className="rs-mono">
              {Array.isArray(v) ? v.join(", ") : typeof v === "object" ? JSON.stringify(v) : String(v)}
            </span>
          </>
        ))}
        {inputs.length ? (
          <>
            <span>{plain ? "uses" : "inputs"}</span>
            <span className={plain ? "" : "rs-mono"}>{inputs.map((i) => (plain && i.text ? i.text : i.label)).join(" · ")}</span>
          </>
        ) : null}
        {node.reads.length ? (
          <>
            <span>reads</span>
            <span className="rs-mono">{node.reads.join(", ")}</span>
          </>
        ) : null}
        {node.writes.length ? (
          <>
            <span>{plain ? "sets" : "writes"}</span>
            <span className="rs-mono">{node.writes.join(", ")}</span>
          </>
        ) : null}
      </div>
      {node.source ? <pre className="rs-inspector-source">{node.source}</pre> : null}
    </div>
  );
}
